export const Role = {
  ADMIN: 'ADMIN',
  /** Генеральный директор: видит всё как ADMIN, но без права изменения (read-only + свои комментарии). */
  GENERAL_DIRECTOR: 'GENERAL_DIRECTOR',
  MANAGER: 'MANAGER',
  PROCUREMENT: 'PROCUREMENT',
  /** Руководитель отдела продаж: видит заявки/клиентов всех менеджеров в своих городах. */
  ROP: 'ROP',
  /** Руководитель отдела закупок: видит квоты/комментарии всех закупщиков (read-only по чужим квотам). */
  ROS: 'ROS',
  /** Помощник: работает как менеджер в рамках заявок/клиентов прикреплённых к нему менеджеров. */
  ASSISTANT: 'ASSISTANT',
  // Роли этапа 3 (с 23.09.2026, docs/stage-3-plan.md, C1). Ни в одну группу ниже не входят:
  // права выдаются точечно по кускам этапа — только там, где роль вписана в белый список
  // (бухгалтер — курсы и факт «Планирования», логист — «Нетарифка», бухгалтер, логист и
  // руководитель склада — публикация новостей).
  /** Бухгалтер: главбух и бухгалтеры — одна роль (права одинаковые). */
  ACCOUNTANT: 'ACCOUNTANT',
  /** Логист: «Нетарифка» на этапе КП (Л1–Л3, модуль customs); доставка и ВЭД — этап 4. */
  LOGIST: 'LOGIST',
  /** Руководитель склада (этап 5). */
  WAREHOUSE_HEAD: 'WAREHOUSE_HEAD',
  /** Приёмка и ОТК (этап 5). */
  WAREHOUSE_QC: 'WAREHOUSE_QC',
  /** Кладовщик (этап 5). */
  WAREHOUSE_KEEPER: 'WAREHOUSE_KEEPER',
} as const

export type Role = (typeof Role)[keyof typeof Role]

// Иерархия читается только в requireRole (API), и он везде требует 'ADMIN'. Порог ниже
// ADMIN через requireRole не ставить: роли этапа 3 (50–60) прошли бы проверку «от
// менеджера» — для доступа по ролям только requireAnyRole с явным списком.
export const ROLE_HIERARCHY: Record<Role, number> = {
  ADMIN: 100,
  GENERAL_DIRECTOR: 90,
  ROP: 70,
  ROS: 70,
  ACCOUNTANT: 60,
  WAREHOUSE_HEAD: 60,
  MANAGER: 50,
  PROCUREMENT: 50,
  LOGIST: 50,
  ASSISTANT: 45,
  WAREHOUSE_QC: 40,
  WAREHOUSE_KEEPER: 40,
}

/** Роли «продажной стороны»: MANAGER + ROP. */
export const SALES_ROLES: Role[] = ['MANAGER', 'ROP']
/** Роли «закупочной стороны»: PROCUREMENT + ROS. */
export const PROCUREMENT_ROLES: Role[] = ['PROCUREMENT', 'ROS']
/** Admin-подобные (видят всё): ADMIN + ROP + ROS (каждый в своей области). */
export const ADMIN_LIKE_ROLES: Role[] = ['ADMIN', 'ROP', 'ROS']

/**
 * Роли, работающие с заявками, клиентами, позициями, КП, квотами и комментариями, —
 * все роли до этапа 3. Белый список для бизнес-адресов API и функций доступа к
 * данным: роль вне списка получает 403 (с 23.09.2026, docs/stage-3-plan.md, C1).
 * Роли этапа 3 сюда НЕ входят — права им выдаются точечно, своим списком на адресе.
 */
export const CRM_CORE_ROLES: Role[] = [
  'ADMIN',
  'GENERAL_DIRECTOR',
  'MANAGER',
  'PROCUREMENT',
  'ROP',
  'ROS',
  'ASSISTANT',
]
export function isCrmCoreRole(role: string): boolean {
  return (CRM_CORE_ROLES as string[]).includes(role)
}

/**
 * Роли этапа 3, которые карточку заявки не открывают, но участвуют в переписке
 * по позиции (с 21.10.2026): их можно @упомянуть в комментарии к позиции, и
 * упомянутый видит и пишет только переписку этой позиции, без веток квот (там
 * цены поставщиков). Логист вдобавок — по позициям своей очереди нетарифки.
 */
export const COMMENT_GUEST_ROLES: Role[] = [
  'ACCOUNTANT',
  'LOGIST',
  'WAREHOUSE_HEAD',
  'WAREHOUSE_QC',
  'WAREHOUSE_KEEPER',
]
export function isCommentGuestRole(role: string): boolean {
  return (COMMENT_GUEST_ROLES as string[]).includes(role)
}

/**
 * Роли с полной видимостью данных «как у ADMIN» (read-уровень):
 * ADMIN — полный доступ, GENERAL_DIRECTOR — только просмотр.
 * Использовать в read-выборках вместо `role === 'ADMIN'`.
 */
export const VIEW_ALL_ROLES: Role[] = ['ADMIN', 'GENERAL_DIRECTOR']
export function isViewAllRole(role: string): boolean {
  return role === 'ADMIN' || role === 'GENERAL_DIRECTOR'
}

export function hasMinRole(actorRole: Role, required: Role): boolean {
  return ROLE_HIERARCHY[actorRole] >= ROLE_HIERARCHY[required]
}

/** Русская расшифровка роли — для UI. */
export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Администратор',
  GENERAL_DIRECTOR: 'Генеральный директор',
  ROP: 'РОП · Руководитель отдела продаж',
  ROS: 'РОЗ · Руководитель отдела закупок',
  MANAGER: 'Менеджер',
  PROCUREMENT: 'Закупщик',
  ASSISTANT: 'Помощник',
  ACCOUNTANT: 'Бухгалтер',
  LOGIST: 'Логист',
  WAREHOUSE_HEAD: 'Руководитель склада',
  WAREHOUSE_QC: 'Приёмка и ОТК',
  WAREHOUSE_KEEPER: 'Кладовщик',
}

// Legacy alias — remove after full migration
export const UserRole = Role
export type UserRole = Role
